import { ChangeDetectionStrategy, Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Subject, distinctUntilChanged, of, skipWhile, takeUntil, first, catchError, tap, mergeMap } from 'rxjs';
import { TypesService } from '../../core/services/types.service';
import { LgvClientService, NotificationService } from '../../core/core.module';
import { AvmPopUpComponent } from '../avm-popup/avm-popup.component';
import { HttpClient } from '@angular/common/http';
import { environment as env } from '../../../environments/environment';
import { LgvAuthMgrService } from '../../core/services/lgv-auth-mgr.service';
import { InfoPopup } from '../info-popup/info-popup.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';


@Component({
    selector: 'lus-adjust-home-value-dialog',
    templateUrl: './adjust-home-value-dialog.component.html',
    styleUrls: ['./adjust-home-value-dialog.component.scss'],
    //changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdjustHomeValueDialogComponent implements OnInit, OnDestroy {
    homeValueForm: UntypedFormGroup;
    adjustmentReason$ = of([]);
    sliderValue = 0;
    sliderMin = 0;
    sliderMax = 0;
    processDate = Date.now();
    isLoading$ = new BehaviorSubject(false);
    maxValue = 0;
    minValue = 0;
    homeValueDetails: any;
    onDestroy$ = new Subject();
    currentAvm: number;

    constructor(public dialogRef: MatDialogRef<AdjustHomeValueDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        private data: any,
        private fb: FormBuilder,
        private typesService: TypesService,
        private notifService: NotificationService,
        private dialog: MatDialog,
        private http: HttpClient,
        private lgvClientService: LgvClientService,
        private authMgr: LgvAuthMgrService
    ) { }

    ngOnInit(): void {
        this.adjustmentReason$ = this.typesService.getRenovationTypes();
        this.homeValueDetails = {
            processedDate: this.lgvClientService.SelectedProperty$.value.propertyInfo.avmprocessedDate,
            estimatedValue: this.lgvClientService.SelectedProperty$.value.propertyInfo.avmestimatedValue,
            lowValue: this.lgvClientService.SelectedProperty$.value.propertyInfo.avmlowValue,
            highValue: this.lgvClientService.SelectedProperty$.value.propertyInfo.avmhighValue,
            source: this.lgvClientService.SelectedProperty$.value?.propertyInfo.avmProvider?.name,
            propertyAdjustments: this.lgvClientService.SelectedProperty$.value?.clientPropertyAdjustments[0]
        };
        if (!this.homeValueDetails?.propertyAdjustments) {
            this.currentAvm = this.homeValueDetails?.estimatedValue;
        } else {
            const percent = this.homeValueDetails?.propertyAdjustments?.adjustmentValue;
            this.currentAvm = this.homeValueDetails?.estimatedValue / (1 + (percent/100));
        }
        this.initFormControls();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next(false);
        this.onDestroy$.complete();
    }

    initFormControls() {
        this.sliderMin = Math.ceil(this.homeValueDetails?.lowValue - (this.homeValueDetails?.lowValue * .1));
        this.sliderMax = Math.ceil(this.homeValueDetails?.highValue + (this.homeValueDetails?.highValue * .1));
        this.sliderValue = this.homeValueDetails?.estimatedValue;
        this.updateTooltip();
        this.homeValueForm = this.fb.group({
            homeValue: [this.homeValueDetails.estimatedValue.toFixed(0), [Validators.required, lessThanHighLimit(this.sliderMax), greaterThanLowLimit(this.sliderMin)]],
            percent: [+this.homeValueDetails?.propertyAdjustments?.adjustmentValue.toFixed(1), Validators.required],
            reason: [this.homeValueDetails?.propertyAdjustments?.avmAdjustmentReasonId.toUpperCase(), Validators.required]
        });
        this.initCalc();
    }

    initCalc() {
        this.homeValueForm.get('homeValue').valueChanges.pipe(
            tap((x) => {
                this.sliderValue = +x;
                this.updateTooltip();
            }),
            skipWhile(() => this.homeValueForm.pristine),
            distinctUntilChanged(),
            takeUntil(this.onDestroy$)
        ).subscribe((x) => {
            this.homeValueForm.markAsPristine();
            const newPercent = this.computePercent(x);
            this.homeValueForm?.controls['percent'].setValue(newPercent);
        });
        this.homeValueForm.get('percent').valueChanges.pipe(
            takeUntil(this.onDestroy$),
            skipWhile(() => this.homeValueForm.pristine),
            distinctUntilChanged()
        ).subscribe((x) => {
            this.homeValueForm.markAsPristine();
            this.updateTooltip();
            const newHomeValue = this.computeNewHomeValue(x);
            this.homeValueForm.controls['homeValue'].setValue(newHomeValue);
        });
    }

    initSlider() {
        const stickerLow = document.querySelector(".sticky-mark-low");
        const stickerHigh = document.querySelector(".sticky-mark-high");
        const slider = document.querySelector(".slider");
        const stepWidth = (this.sliderMax - this.sliderMin) / slider.clientWidth;
        const lowPosition = ((this.homeValueDetails?.lowValue - this.sliderMin) * stepWidth) + 20;
        const highPosition = ((this.homeValueDetails?.highValue - this.sliderMin) * stepWidth) + 20;
        stickerLow['style'].left = Math.round(lowPosition) + "px";
        stickerHigh['style'].left = Math.round(highPosition) + "px";

    }

    computeNewHomeValue(percent: number) {
        return +(((this.currentAvm * percent) / 100) + this.currentAvm).toFixed(0);
    }

    computePercent(newHomeValue: number) {
        return +(((newHomeValue - this.currentAvm) * 100) / this.currentAvm).toFixed(1);
    }

    resetCalc() {
        const isNewAdjustment = !this.homeValueDetails?.propertyAdjustments;
        const clientId = this.lgvClientService.SnapShot$.value?.clientInfo?.id;
        const propertyId = this.lgvClientService.SelectedProperty$.value?.propertyInfo?.id;
        const adjustmentId = this.homeValueDetails?.propertyAdjustments?.id;

        if (isNewAdjustment) {
            this.homeValueForm?.controls['homeValue'].setValue(this.homeValueDetails?.estimatedValue);
            this.homeValueForm?.controls['percent'].setValue(0);
            this.homeValueForm?.controls['reason'].setValue(null);
            this.sliderValue = this.homeValueDetails?.estimatedValue;
            this.updateTooltip();
        } else {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '500px',
                data: {
                    header: 'Reset AVM',
                    Confirmation: `By clicking reset, you will remove the manual adjustment and the values shown in your dashboard will pull from our data sources.
                    <br><br>
                    You will be able to adjust the value if you want to change it again.
                    `,
                    button: 'Reset AVM'
                }
            });

            dialogRef.afterClosed().pipe(
                mergeMap((data) => {
                    this.isLoading$.next(true);
                    if (data?.action) {
                        return this.lgvClientService.resetHomeValue(clientId, propertyId, adjustmentId).pipe(first(), catchError(() => of('error')));
                    } else {
                        return of(null);
                    }
                })
            ).subscribe(x => {
                if (x === 'error') {
                    this.notifService.success('Failed to reset home value adjustment.')
                } else if (x !== null) {
                    this.loadSnapshot();
                    this.notifService.success('Home value adjustment has been reset.');
                }

                this.isLoading$.next(false);
            });
        }
    }

    sliderValueChanged(value) {
        const percent = this.computePercent(value);
        this.homeValueForm?.controls['homeValue'].setValue(value);
        this.homeValueForm?.controls['percent'].setValue(percent);
        this.updateTooltip();
    }

    onClose() {
        this.dialogRef.close();
    }

    onSave() {
        this.isLoading$.next(true);
        //save
        const clientId = this.lgvClientService.SnapShot$.value?.clientInfo?.id;
        const propertyId = this.lgvClientService.SelectedProperty$.value?.propertyInfo?.id;
        const model = {
            adjustmentTypeId: 'B8FB5087-A355-EE11-9937-6045BD096012',
            avmAdjustmentReasonId: this.homeValueForm?.controls['reason'].value,
            adjustmentValue: this.getExactPercentage()
        };
        const isNewAdjustment = !this.homeValueDetails?.propertyAdjustments;
        const adjustmentId = this.homeValueDetails?.propertyAdjustments?.id;

        this.lgvClientService.adjustHomeValue(clientId, propertyId, model, isNewAdjustment, adjustmentId).pipe(
            takeUntil(this.onDestroy$),
            catchError((e) => { this.notifService.error('There was an error adjusting your home value. Please try again.'); return of('error'); })
        ).subscribe((error) => {
            this.isLoading$.next(false);
            if (error !== 'error') {
                this.notifService.success('Home value successfully adjusted.');
            }
            this.loadSnapshot();
        });

    }

    loadSnapshot() {
        if (this.authMgr.IsClient) {
            // If user is both client and secureClient, two auth events will fire.  Only load snapshot for IsClient
            if (!this.authMgr.IsSecureClient) {
                this.lgvClientService.LoadSnapshotForClient();
            }
        }
        else if (this.lgvClientService.SavedImpersonatedClientID) {
            this.lgvClientService.LoadSnapshotForNonClient(this.lgvClientService.SavedImpersonatedClientID, true);
        }
        setTimeout(() => {
            this.dialogRef.close();
            //location.reload();
        }, 500)
    }

    updateTooltip() {
        const tooltip = document.querySelector(".current-value");
        const slider = document.querySelector(".slider");
        const steps = (this.sliderMax - this.sliderMin) / slider.clientWidth;

        tooltip.childNodes[0].textContent = '$ ' + this.sliderValue?.toString();

        let currentStep = ((this.sliderValue - this.sliderMin) / steps) - 50;
        if (currentStep > (slider.clientWidth - 100)) {
            currentStep = slider.clientWidth - 100;
        } else if (currentStep < slider.clientLeft) {
            currentStep = slider.clientLeft;
        } 
        // Reposition tooltip on top of the thumb
        tooltip['style'].left = Math.round(currentStep) + "px";

    }

    setHighValue() {
        this.homeValueForm.controls['homeValue'].setValue(this.homeValueDetails?.highValue);
        this.sliderValue = this.homeValueDetails?.highValue;
        this.updateTooltip();
    }

    setLowValue() {
        this.homeValueForm.controls['homeValue'].setValue(this.homeValueDetails?.lowValue);
        this.sliderValue = this.homeValueDetails?.lowValue;
        this.updateTooltip();
    }

    showAvmDialog() {
        this.dialogRef.close();
        const dialogRef = this.dialog.open(AvmPopUpComponent, {
            width: '500px',
            data: {
                isValuationEstimateOwner: true,
                isValuationEstimate: false,
                isValuationHistory: false,
                isFromAdjustHomeValue: true
            }
        });
    }

    getExactPercentage() {
        return ((this.homeValueForm.controls['homeValue'].value - this.currentAvm) / this.currentAvm) * 100;
    }
}

export function lessThanHighLimit(limit: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        return value >= limit ? { 'greaterThanHighLimit': true } : null;

    }
}

export function greaterThanLowLimit(limit: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        return value <= limit ? { 'lessThanLowLimit': true } : null;

    }
}